import { REPORTS } from "../../api/reports";

/**
 * The report catalogue, derived from what radreportsapi's handlers actually
 * read — not from what the PHP screens happen to show.
 *
 * Each handler was read to establish which filter fields it consumes
 * (app/handler/reports.go). That produces three groups, and the grouping is
 * the design:
 *
 *   scope-only    the namespace from the token is the whole request
 *   filtered      one or two operator-supplied inputs, all optional or simple
 *   drill-down    requires a customerid / accountid / groupuid UUID
 *
 * 🔑 The drill-downs are deliberately NOT nav entries. A screen whose only
 * input is a UUID cannot be reached by typing; it is opened FROM a row of
 * another report. Shipping them as top-level pages would produce six screens
 * that can only ever show an error, which is worse than not shipping them.
 */

export type FilterField = "start" | "end" | "username" | "mobile" | "group_by";

export interface ReportSpec {
  /** Route path under /reports/. */
  slug: string;
  title: string;
  endpoint: string;
  /** Feature flag key. A screen ships dark and is switched on per tenant. */
  flag: string;
  fields: FilterField[];
  /**
   * Fields the SERVICE rejects the request without — transcribed from the
   * handlers' own StatusBadRequest guards, not guessed from what looks
   * sensible. A report with these unset must not be sent at all: firing it on
   * mount produces a 400 before the operator has been given anything to fill
   * in, which reads as a broken screen rather than an unfinished form.
   */
  required?: FilterField[];
  /** Shown above the table so an empty result is interpretable. */
  note?: string;
  /**
   * The answer is an object and its rows live under this key (Collections
   * answers {rows, total, count}); absent means the answer is the row list.
   */
  rowsKey?: string;
  /** All plans: add each plan's price in force from billing (owner 2026-10-29). */
  planPrices?: boolean;
}

export const REPORT_SPECS: ReportSpec[] = [
  {
    slug: "active-sessions",
    title: "Active sessions",
    endpoint: REPORTS.activeSessions,
    flag: "reports.activeSessions",
    fields: [],
    note: "Who is online now — sessions with no stop time whose accounting was updated in the last 5 minutes. An empty table means nobody is currently online in this tenant, not that the report failed.",
  },
  {
    slug: "plans",
    title: "Plans",
    endpoint: REPORTS.plans,
    flag: "reports.plans",
    fields: [],
    note: "Active plans in the catalogue for your tenant and its descendants. Price incl. GST is what billing charges now (Plans › Price list).",
    planPrices: true,
  },
  {
    slug: "active-plans",
    title: "Active subscriptions",
    endpoint: REPORTS.activePlans,
    flag: "reports.activePlans",
    fields: [],
  },
  {
    slug: "expiring-plans",
    required: ["start", "end"],
    title: "Expiring plans",
    endpoint: REPORTS.expiringPlans,
    flag: "reports.expiringPlans",
    fields: ["start", "end"],
  },
  {
    slug: "recharge-log",
    required: ["start", "end"],
    title: "Recharge log",
    endpoint: REPORTS.rechargeLogAll,
    flag: "reports.rechargeLog",
    fields: ["start", "end"],
  },
  {
    slug: "recharge-log-by-user",
    required: ["start", "end"],
    title: "Recharge log by user",
    endpoint: REPORTS.rechargeLogByUser,
    flag: "reports.rechargeLogByUser",
    fields: ["username", "start", "end"],
  },
  {
    slug: "user-sessions",
    required: ["start", "end"],
    title: "User sessions",
    endpoint: REPORTS.userSessions,
    flag: "reports.userSessions",
    fields: ["username", "start", "end"],
  },
  {
    slug: "customer-identity",
    title: "Customer lookup",
    endpoint: REPORTS.customerIdentity,
    flag: "reports.customerIdentity",
    fields: ["mobile"],
  },
  {
    slug: "sms-log",
    title: "SMS log",
    endpoint: REPORTS.smsLog,
    flag: "reports.smsLog",
    fields: ["mobile", "start", "end"],
  },
  // Subscriber-management reports (owner 2026-10-24). Dates default to the
  // last 30 days; the presets pick 7/30/60/90/180/365 days or a month.
  {
    slug: "expired-not-renewed",
    title: "Expired, not renewed",
    endpoint: REPORTS.expiredNotRenewed,
    flag: "reports.expiredNotRenewed",
    fields: ["start", "end"],
    note: "Subscribers whose plan ended between From and To and who hold no plan that is still running — your win-back call list.",
  },
  {
    slug: "failed-logins",
    title: "Failed logins",
    endpoint: REPORTS.failedLogins,
    flag: "reports.failedLogins",
    fields: ["start", "end"],
    note: "Rejected logins per username, newest reject and newest accept in IST. \"Still failing\" means the last attempt was rejected. Typed passwords are never stored.",
  },
  {
    slug: "collections",
    title: "Collections summary",
    endpoint: REPORTS.collections,
    flag: "reports.collections",
    fields: ["start", "end", "group_by"],
    rowsKey: "rows",
    note: "Successful recharges summed per day or month and payment method. Reversed and failed recharges are not counted.",
  },
  {
    slug: "funds-statement",
    title: "Funds statement",
    endpoint: REPORTS.fundsStatement,
    flag: "reports.fundsStatement",
    fields: ["start", "end"],
    note: "Confirmed funds per sub-operator: balance at From, money in, money out, balance at To (₹). Pending top-ups and holds are not money moved.",
  },
  {
    slug: "new-subscribers",
    title: "New subscribers",
    endpoint: REPORTS.newSubscribers,
    flag: "reports.newSubscribers",
    fields: ["start", "end"],
    note: "Subscribers registered between From and To, newest first.",
  },
  // Phase 2 (owner 2026-10-24).
  {
    slug: "renewals-by-plan",
    title: "Renewals by plan",
    endpoint: REPORTS.renewalsByPlan,
    flag: "reports.renewalsByPlan",
    fields: ["start", "end"],
    note: "Recharges per plan between From and To. A renewal is a recharge by a subscriber who had paid before; the rest are first recharges.",
  },
  {
    slug: "never-used",
    title: "Never used",
    endpoint: REPORTS.neverUsed,
    flag: "reports.neverUsed",
    fields: ["start", "end"],
    note: "Subscribers whose plan is running but who started no session between From and To — paying but not using, the likeliest to leave.",
  },
  {
    slug: "top-usage",
    title: "Top usage",
    endpoint: REPORTS.topUsage,
    flag: "reports.topUsage",
    fields: ["start", "end"],
    note: "The 100 heaviest users by data, from sessions that started between From and To.",
  },
  {
    slug: "peak-hours",
    title: "Peak hours",
    endpoint: REPORTS.peakHours,
    flag: "reports.peakHours",
    fields: ["start", "end"],
    note: "Sessions started in each hour of the day (IST) between From and To, all 24 hours — for capacity and maintenance windows.",
  },
  {
    slug: "payment-failures",
    title: "Payment failures",
    endpoint: REPORTS.paymentFailures,
    flag: "reports.paymentFailures",
    fields: ["start", "end"],
    note: "Online payments started between From and To that were not completed, with the last gateway event.",
  },
  {
    slug: "promo-use",
    title: "Promotion code use",
    endpoint: REPORTS.promoUse,
    flag: "reports.promoUse",
    fields: ["start", "end"],
    note: "Every promotion code with its redemptions between From and To. Discount and bonus days count only redemptions that were paid.",
  },
  {
    slug: "base-snapshot",
    title: "Subscriber base",
    endpoint: REPORTS.baseSnapshot,
    flag: "reports.baseSnapshot",
    fields: [],
    note: "Your subscribers right now, per site: registered, active, expired, never activated and disabled.",
  },
  {
    slug: "staff-activity",
    title: "Staff activity",
    endpoint: REPORTS.staffActivity,
    flag: "reports.staffActivity",
    fields: ["start", "end"],
    note: "Recharges, funds entries and PIN orders between From and To per login that recorded them. \"(not recorded)\" rows carry no login.",
  },
  {
    slug: "tenant-summary",
    title: "Tenant summary",
    endpoint: REPORTS.tenantSummary,
    flag: "reports.tenantSummary",
    fields: [],
    note: "Platform tenants inside your scope with their running subscribers, this month's recharges and SMS.",
  },
];

/**
 * Reports that require a UUID and are therefore reached from a row, not from
 * the nav. Kept here rather than dropped, so the next person can see that the
 * remaining six endpoints are accounted for and why they are not screens.
 */
export const DRILL_DOWN_ENDPOINTS = [
  REPORTS.customerPlan,
  REPORTS.customerRecharges,
  REPORTS.customerSessionsDaily,
  REPORTS.customerSessionsSummary,
  REPORTS.customerDevices,
  REPORTS.planDetail,
] as const;
